<template>
  <div v-if="!isNativePlatform">
    <AdaptiveSheet
      class="ScanQrCodeNoCordova"
      swipe-to-close
      :tablet-fullscreen="false"
      :backdrop="false"
      full-screen
      @open="scanImage()"
      @close="stopScan()"
    >
      <f7-page>
        <div class="scanner-shell">
          <div id="video-container" ref="videoContainer" class="avocash-style">
            <video id="qr-video" ref="video"></video>
            <f7-link
              class="file-scan-link"
              @click="scanQRCodeFile()"
              icon-ios="f7:qrcode_viewfinder"
              icon-aurora="f7:qrcode_viewfinder"
              icon-md="material:qr_code"
            ></f7-link>
            <input
              type="file"
              id="qr-input-file"
              accept="image/*"
              style="display: none"
            />
          </div>
          <f7-block v-if="resultQR">
            <p>{{ resultQR }}</p>
          </f7-block>
        </div>
        <template #fixed>
          <f7-fab
            v-if="hasTorch"
            :style="{
              bottom: '0px',
            }"
            position="left-bottom"
            @click="activateTorch()"
            style="background-color: transparent"
          >
            <f7-icon
              v-if="torchActive"
              ios="material:flash_on"
              aurora="material:flash_on"
              md="material:flash_on"
              color="white"
            ></f7-icon>
            <f7-icon
              v-else
              ios="material:flash_off"
              aurora="material:flash_off"
              md="material:flash_off"
              color="white"
            >
            </f7-icon>
          </f7-fab>
          <f7-fab
            position="center-bottom"
            @click="closePopup()"
            :style="{
              bottom: '0px',
            }"
          >
            <f7-icon ios="f7:multiply" md="material:close" color="white"></f7-icon>
          </f7-fab>
          <f7-fab
            :style="{
              bottom: '0px',
            }"
            v-if="cameraSupport2Cameras && cameraSupport2Cameras.length >= 2"
            position="right-bottom"
            color="none"
            @click="switchCamera()"
            style="background-color: transparent"
          >
            <f7-icon
              ios="f7:camera_rotate"
              aurora="f7:camera_rotate"
              md="f7:camera_rotate"
              color="white"
            ></f7-icon>
          </f7-fab>
        </template>
      </f7-page>
    </AdaptiveSheet>
    <AdaptiveSheet
      class="notAvocashQRCODE"
      style="height: auto; --f7-popup-bg-color: #fff"
      swipe-to-close
      close-on-escape
      close-by-outside-click
      :backdrop="false"
      full-screen
    >
      <div class="scanner-shell">
        <f7-block class="scanner-result">
          <p>{{ resultQR }}</p>
        </f7-block>
      </div>
    </AdaptiveSheet>
  </div>
</template>
<style scoped>
.fab > a,
.fab-buttons a {
  background: none;
  box-shadow: none;
}

#video-container.avocash-style .scan-region-highlight-svg,
#video-container.avocash-style .code-outline-highlight {
  stroke: var(--f7-theme-color) !important;
}

.scan-region-highlight-svg {
  stroke: var(--f7-theme-color) !important;
}

.code-outline-highlight {
  stroke: var(--f7-theme-color) !important;
}

.scanner-shell {
  box-sizing: border-box;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 24px 16px;
}

#video-container.avocash-style {
  position: relative;
  width: 100%;
  height: 60vh;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 12px;
  background: #000;
}

#qr-video {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  display: block;
}

.file-scan-link {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 50%;
}

.scanner-result {
  text-align: center;
}

/* On large screens (web/PWA desktop) keep the camera view inside the centered
   shell and scale it to the viewport instead of stretching full screen. */
@media (min-width: 1024px) {
  #video-container.avocash-style {
    width: min(100%, 520px);
    height: auto;
    aspect-ratio: 4 / 3;
    max-height: 78vh;
  }
}
</style>
<script setup>
import { theme, f7 } from "framework7-vue";
import AdaptiveSheet from "./common/AdaptiveSheet.vue";
import { closeAdaptiveModal } from "../js/composables/useAdaptiveModal.js";
import { useScanQRCode } from "@/js/composables/useScanQRCode.js";
import { ref, computed, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useAccountStore } from "../js/pinia-stores/account.js";
import { useAuthStore } from "../js/pinia-stores/auth.js";
import { useShopStore } from "../js/pinia-stores/shop.js";
import QrScanner from "qr-scanner";
import _ from "lodash";

const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const accountStore = useAccountStore();
const authStore = useAuthStore();

const shopStore = useShopStore();

const { primaryAccount, businessAccount } = storeToRefs(accountStore);
const { getProfile, isAuthenticated, isProfileLoaded, profile, isNativePlatform } = storeToRefs(authStore);

const { shop } = storeToRefs(shopStore);

const { checkQrCode, scanQRCode, scanImageFromQr } = useScanQRCode({ f7route: props.f7route, f7router: props.f7router });

const resultQR = ref("");
const torchActive = ref(false);
const hasTorch = ref(false);
const cameraSupport2Cameras = ref([]);
const permission_name = ref("camera");
const permission = ref(false);
const cameraPermissionStatus = ref("");
const techPermission = ref({});
const camera = ref("");
const qrUrl = ref("");
const front = ref("false");
const qrcode = ref("");
const videoContainer = ref(null);
const video = ref(null);
const scanner = ref(null);
const camnum = ref(0);

function updateStyle() {
  if (isNativePlatform.value || !videoContainer.value) {
    return;
  }
  videoContainer.value.className = "avocash-style";
  scanner.value?._updateOverlay();
  scanner.value?.setInversionMode("both");
}

function activateTorch() {
  if (isNativePlatform.value || !scanner.value) {
    return;
  }
  torchActive.value = !torchActive.value;
  scanner.value
    .toggleFlash()
    .then(() => (torchActive.value = scanner.value.isFlashOn()));
}

function updateFlashAvailability() {
  if (isNativePlatform.value || !scanner.value) {
    return;
  }
  scanner.value.hasFlash().then((hasFlash) => {
    hasTorch.value = hasFlash;
  });
}

function scanImage() {
  if (isNativePlatform.value || !scanner.value) {
    return;
  }
  scanner.value.start().then(() => {
    updateFlashAvailability();
    QrScanner.listCameras(true).then((cameras) => {
      cameraSupport2Cameras.value = cameras;
    });
  });
}

function switchCamera() {
  if (isNativePlatform.value || !scanner.value || !cameraSupport2Cameras.value.length) {
    return;
  }
  scanner.value
    .setCamera(cameraSupport2Cameras.value[camnum.value++].id)
    .then(updateFlashAvailability());
  if (camnum.value === cameraSupport2Cameras.value.length) {
    camnum.value = 0;
  }
}

function closePopup() {
  if (isNativePlatform.value) {
    return;
  }
  resultQR.value = "";
  if (f7.$("#qr-canvas-visible")) {
    f7.$("#qr-canvas-visible").hide();
  }
  closeAdaptiveModal(".ScanQrCodeNoCordova");
  scanner.value?.stop();
}

async function scanQRCodeFile() {
  if (isNativePlatform.value) {
    return;
  }
  if (f7.$("#qr-canvas-visible")) {
    f7.$("#qr-canvas-visible").hide();
    resultQR.value = "";
  }
  stopScan();
  document.querySelector("#qr-input-file").click();
  const fileinput = document.getElementById("qr-input-file");
  fileinput.addEventListener("change", (e) => {
    const imageFile = e.target.files[0];
    if (e.target.files.length == 0) {
      return;
    }
    console.log(imageFile);
    QrScanner.scanImage(imageFile, { returnDetailedScanResult: true })
      .then((result) => checkQrCode(result.data))
      .catch((err) => {
        console.log(`Error scanning file. Reason: ${err}`);
        resultQR.value = `Error scanning file. Reason: ${err}`;
      });
  });
}

function stopScan() {
  if (isNativePlatform.value) {
    return;
  }
  scanner.value?.stop();
  f7.$("#qr-canvas-visible").hide();
  resultQR.value = "";
}

onMounted(() => {
  if (!video.value) {
    return;
  }
  scanner.value = new QrScanner(
    video.value,
    (result) => checkQrCode(result.data),
    {
      onDecodeError: (error) => {
        console.log(error);
      },
      highlightScanRegion: true,
      highlightCodeOutline: true,
    }
  );
  updateStyle();
});
</script>
